import React, { useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { formatReportDateTime, formatReportDuration, formatReportTime } from '../../utils/reportFormat';

/** A report record of any kind (same shape as the Reports "All" timeline). */
export interface ReportTimelineLike {
  kind: 'message' | 'call' | 'visit';
  timestamp: string;
  data: any;
}

export interface ReportContactGroup {
  key: string;
  name: string;
  phone?: string;
  avatar?: string;
  items: ReportTimelineLike[];
  messages: number;
  calls: number;
  answeredCalls: number;
  missedCalls: number;
  talkSeconds: number;
  visits: number;
  completedVisits: number;
  lastAt: string;
}

const ANSWERED = ['answered', 'completed', 'ended'];

/** Same customer across messages, calls and visits: matched on the phone
 * number when it's readable (masked numbers aren't), else the contact id. */
function contactKey(item: ReportTimelineLike): { key: string; name: string; phone?: string; avatar?: string } {
  const d = item.data || {};
  const phone: string | undefined = item.kind === 'message' ? d.contactPhone : d.phoneNumber;
  const digits = String(phone || '').replace(/\D/g, '');
  const name: string = d.contactName || phone || d.contactId || '—';
  if (digits.length >= 8 && !String(phone).includes('*')) return { key: `tel:${digits.slice(-9)}`, name, phone, avatar: d.contactAvatar };
  return { key: `id:${d.contactId || name}`, name, phone, avatar: d.contactAvatar };
}

export function groupReportItemsByContact(items: ReportTimelineLike[]): ReportContactGroup[] {
  const groups = new Map<string, ReportContactGroup>();
  for (const item of items) {
    const { key, name, phone, avatar } = contactKey(item);
    let g = groups.get(key);
    if (!g) {
      g = { key, name, phone, avatar, items: [], messages: 0, calls: 0, answeredCalls: 0, missedCalls: 0, talkSeconds: 0, visits: 0, completedVisits: 0, lastAt: item.timestamp };
      groups.set(key, g);
    }
    // Prefer a real name over a bare number once one shows up
    if ((!g.name || g.name === g.phone) && item.data?.contactName) g.name = item.data.contactName;
    if (!g.avatar && avatar) g.avatar = avatar;
    g.items.push(item);
    if (new Date(item.timestamp).getTime() > new Date(g.lastAt).getTime()) g.lastAt = item.timestamp;
    if (item.kind === 'message') g.messages++;
    else if (item.kind === 'call') {
      g.calls++;
      const answered = ANSWERED.includes(item.data.status) && (item.data.duration || 0) > 0;
      if (answered) {
        g.answeredCalls++;
        g.talkSeconds += item.data.duration || 0;
      } else if (item.data.direction === 'inbound') {
        g.missedCalls++;
      }
    } else {
      g.visits++;
      if (item.data.status === 'completed') g.completedVisits++;
    }
  }
  groups.forEach((g) => g.items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));
  return [...groups.values()];
}

/** "Break 2m 00s before" — idle time since the agent's previous call or
 * message thread ended (same day). Renders nothing when the record has no
 * break info (e.g. a follow-up message in the same thread). */
export const ReportBreakLine: React.FC<{ seconds?: number | null }> = ({ seconds }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  if (seconds === undefined) return null;
  const units = { s: t('reports.duration.s'), m: t('reports.duration.m'), h: t('reports.duration.h'), d: t('reports.duration.d') };
  return (
    <View style={[styles.breakLine, { flexDirection: rtl.rowDirection }]}>
      <Ionicons name="cafe-outline" size={11} color={colors.text.secondary as string} />
      <Text style={styles.breakText} numberOfLines={1}>
        {seconds === null ? t('reports.break.firstOfDay') : t('reports.break.before', { time: formatReportDuration(seconds, units) })}
      </Text>
    </View>
  );
};

interface RowProps {
  group: ReportContactGroup;
  index: number;
  expanded: boolean;
  onToggle: () => void;
}

/** One customer: totals across their messages, calls and visits; tap to see each record. */
export const ReportContactGroupRow: React.FC<RowProps> = ({ group, index, expanded, onToggle }) => {
  const styles = useThemedStyles(getStyles);
  const { t, locale } = useLocale();
  const rtl = useRTLStyles();
  const units = useMemo(() => ({ s: t('reports.duration.s'), m: t('reports.duration.m'), h: t('reports.duration.h'), d: t('reports.duration.d') }), [t]);

  const chip = (icon: string, text: string, tone?: 'good' | 'bad') => (
    <View style={[styles.chip, tone === 'good' && styles.chipGood, tone === 'bad' && styles.chipBad, { flexDirection: rtl.rowDirection }]}>
      <Ionicons name={icon as any} size={11} color={colors.text.secondary as string} />
      <Text style={styles.chipText}>{text}</Text>
    </View>
  );

  const describe = (item: ReportTimelineLike): { icon: string; text: string; time: string } => {
    const d = item.data;
    if (item.kind === 'message') {
      return {
        icon: d.direction === 'inbound' ? 'arrow-down-circle-outline' : 'arrow-up-circle-outline',
        text: `${d.content || d.type || ''}${d.agentName ? ` · ${d.agentName}` : ''}`,
        time: formatReportDateTime(d.timestamp, locale),
      };
    }
    if (item.kind === 'call') {
      return {
        icon: 'call-outline',
        text: `${d.direction} · ${d.status} · ${formatReportDuration(d.duration || 0, units)}${d.agentName ? ` · ${d.agentName}` : ''}`,
        time: `${formatReportDateTime(d.startedAt, locale)}${d.endedAt ? ` → ${formatReportTime(d.endedAt, locale)}` : ''}`,
      };
    }
    return {
      icon: 'location-outline',
      text: `${d.status}${d.outcomeLabel ? ` · ${d.outcomeLabel}` : ''}${d.agentName ? ` · ${d.agentName}` : ''}`,
      time: formatReportDateTime(d.startedAt || d.scheduledFor, locale),
    };
  };

  return (
    <View style={[styles.card, expanded && styles.cardExpanded]}>
      <TouchableOpacity style={[styles.row, { flexDirection: rtl.rowDirection }]} onPress={onToggle} activeOpacity={0.7}>
        <View style={styles.indexBadge}><Text style={styles.indexText}>{index + 1}</Text></View>
        {group.avatar ? (
          <Image source={{ uri: group.avatar }} style={styles.avatar} />
        ) : (
          <View style={styles.avatarFallback}><Text style={styles.avatarText}>{(group.name || '?').trim().charAt(0).toUpperCase()}</Text></View>
        )}
        <View style={styles.info}>
          <Text style={[styles.name, { textAlign: rtl.textStart }]} numberOfLines={1}>
            {group.name}{group.phone && group.phone !== group.name ? ` · ${group.phone}` : ''}
          </Text>
          <Text style={[styles.time, { textAlign: rtl.textStart }]} numberOfLines={1}>{formatReportDateTime(group.lastAt, locale)}</Text>
          <View style={[styles.chips, { flexDirection: rtl.rowDirection }]}>
            {group.messages > 0 && chip('chatbubbles-outline', t('reports.group.messages', { count: group.messages }))}
            {group.calls > 0 && chip('call-outline', t('reports.group.calls', { count: group.calls, answered: group.answeredCalls }))}
            {group.missedCalls > 0 && chip('call-outline', t('reports.group.missed', { count: group.missedCalls }), 'bad')}
            {group.talkSeconds > 0 && chip('time-outline', formatReportDuration(group.talkSeconds, units))}
            {group.visits > 0 && chip('location-outline', t('reports.group.visits', { count: group.visits, completed: group.completedVisits }), group.completedVisits ? 'good' : undefined)}
          </View>
        </View>
        <View style={styles.trailing}>
          <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={16} color={colors.text.secondary as string} />
        </View>
      </TouchableOpacity>
      {expanded && (
        <View style={styles.children}>
          {group.items.map((item, i) => {
            const d = describe(item);
            return (
              <View key={i} style={[styles.child, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name={d.icon as any} size={14} color={colors.primary as string} />
                <View style={styles.childBody}>
                  <Text style={[styles.childText, { textAlign: rtl.textStart }]} numberOfLines={2}>{d.text}</Text>
                  <Text style={[styles.childTime, { textAlign: rtl.textStart }]}>{d.time}</Text>
                  {item.kind !== 'visit' && <ReportBreakLine seconds={item.data?.breakBeforeSeconds} />}
                </View>
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  card: { borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border },
  cardExpanded: { backgroundColor: colors.background.secondary },
  row: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, paddingHorizontal: spacing.sm },
  indexBadge: { minWidth: 22, alignItems: 'center' },
  indexText: { color: colors.text.disabled, fontSize: typography.fontSize.xs },
  avatar: { width: 36, height: 36, borderRadius: 18 },
  avatarFallback: { width: 36, height: 36, borderRadius: 18, backgroundColor: colors.background.secondary, alignItems: 'center', justifyContent: 'center' },
  avatarText: { color: colors.text.secondary, fontWeight: '700' },
  info: { flex: 1, minWidth: 0, gap: 4 },
  name: { color: colors.text.primary, fontWeight: '600', fontSize: typography.fontSize.sm },
  chips: { flexWrap: 'wrap', gap: 4 },
  chip: { alignItems: 'center', gap: 3, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 10, backgroundColor: colors.background.secondary },
  chipGood: { backgroundColor: 'rgba(16,185,129,0.12)' },
  chipBad: { backgroundColor: 'rgba(239,68,68,0.12)' },
  chipText: { color: colors.text.secondary, fontSize: 11 },
  trailing: { alignItems: 'center', justifyContent: 'center' },
  time: { color: colors.text.secondary, fontSize: typography.fontSize.xs },
  children: { paddingHorizontal: spacing.md, paddingBottom: spacing.sm, gap: 6 },
  child: { alignItems: 'flex-start', gap: spacing.xs },
  childBody: { flex: 1, minWidth: 0, gap: 2 },
  childText: { color: colors.text.primary, fontSize: typography.fontSize.xs },
  breakLine: { alignItems: 'center', gap: 4, alignSelf: 'flex-start' },
  breakText: { color: colors.text.secondary, fontSize: 11 },
  childTime: { color: colors.text.secondary, fontSize: 11 },
});
